import { memo, useMemo, type MouseEvent } from 'react';
import type { InventoryItem, SlotSection, SlotLocation, ContainerCapacity } from '../types';
import { locationsEqual } from '../utils/location';
import { ItemSlot } from './ItemSlot';

interface Props {
  items: (InventoryItem | null)[];
  section: SlotSection;
  columns?: number;
  capacity: ContainerCapacity;
  selectedLocation?: SlotLocation | null;
  onItemClick?: (item: InventoryItem, location?: SlotLocation) => void;
  onItemRightClick?: (e: MouseEvent, item: InventoryItem, location?: SlotLocation) => void;
}

export const InventoryGrid = memo(function InventoryGrid({
  items,
  section,
  columns = 5,
  capacity,
  selectedLocation,
  onItemClick,
  onItemRightClick,
}: Props) {
  const paddedItems = useMemo<(InventoryItem | null)[]>(() => [
    ...items,
    ...Array(Math.max(0, capacity.maxSlots - items.length)).fill(null),
  ].slice(0, capacity.maxSlots), [items, capacity.maxSlots]);

  return (
    <div
      className="grid gap-1"
      style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {paddedItems.map((item, i) => {
        const loc: SlotLocation = { section, index: i };
        return (
          <div key={i} className="aspect-square">
            <ItemSlot
              item={item}
              location={loc}
              isActive={!!selectedLocation && locationsEqual(selectedLocation, loc)}
              isUnavailable={i >= capacity.usableSlots}
              onClick={onItemClick}
              onRightClick={onItemRightClick}
            />
          </div>
        );
      })}
    </div>
  );
});
